import { el, qs } from '../utils/dom.js';
import { PROPERTY_PRESETS, resolvePresetKey, getPresets, ENGINE_LABELS } from './PropertyPresets.js';

/**
 * Property editor panel — edit custom properties on maps, layers, and objects.
 * Supports bool, int, float, string, color types.
 * Includes engine-specific property presets.
 */
export class PropertyEditor {
  constructor(state) {
    this.state = state;
    this.container = qs('#property-list');
    this._target = null; // { type: 'map' | 'layer' | 'object', ref }

    state.onChange('activeLayerIndex', () => this._showLayerProperties());
    state.onChange('layers', () => this._showLayerProperties());

    this._showLayerProperties();
  }

  /** Show properties for the active layer. */
  _showLayerProperties() {
    const layer = this.state.getActiveLayer();
    if (!layer) { this.container.innerHTML = ''; return; }
    this._target = { type: 'layer', ref: layer };
    this._renderProperties(layer.properties || {});
  }

  /** Show properties for a specific object. */
  showObjectProperties(obj) {
    this._target = { type: 'object', ref: obj };
    this._renderProperties(obj.properties || {});
  }

  /** Show map-level properties. */
  showMapProperties() {
    this._target = { type: 'map', ref: null };
    this._renderProperties(this.state.get('properties') || {});
  }

  _renderProperties(props) {
    this.container.innerHTML = '';

    // Engine presets dropdown
    this._renderEnginePresetsBar();

    for (const [name, value] of Object.entries(props)) {
      const propType = value?.type || typeof value;
      const propValue = value?.value !== undefined ? value.value : value;
      const description = value?.description || this._lookupDescription(name);
      const row = this._createPropertyRow(name, propType, propValue, description);
      this.container.appendChild(row);
    }

    // Add property button
    const addBtn = el('button', {
      className: 'btn btn-sm prop-add-btn',
      onClick: () => this._addPropertyWithSuggestions(),
    }, '+ Add Property');
    this.container.appendChild(addBtn);
  }

  /** Render the "Add Engine Properties" dropdown bar. */
  _renderEnginePresetsBar() {
    const bar = el('div', { className: 'prop-engine-bar' });

    const targetEngine = this.state.get('targetEngine');
    const resolvedKey = resolvePresetKey(targetEngine);

    // Build ordered list: suggested engine first, then the rest
    const engines = [...ENGINE_LABELS];
    if (resolvedKey) {
      const idx = engines.findIndex(e => e.key === resolvedKey);
      if (idx > 0) {
        const [suggested] = engines.splice(idx, 1);
        engines.unshift(suggested);
      }
    }

    const select = el('select', { className: 'prop-engine-select' });
    select.appendChild(el('option', { value: '' }, 'Add Engine Properties...'));
    for (const eng of engines) {
      const label = resolvedKey === eng.key
        ? `${eng.label} (suggested)`
        : eng.label;
      select.appendChild(el('option', { value: eng.key }, label));
    }

    select.addEventListener('change', () => {
      const engineKey = select.value;
      if (!engineKey) return;
      this._applyEnginePresets(engineKey);
      select.value = '';
    });

    bar.appendChild(select);
    this.container.appendChild(bar);
  }

  /** Batch-add all preset properties for an engine to the current target. */
  _applyEnginePresets(engineKey) {
    if (!this._target) return;
    const targetType = this._target.type;
    const presets = getPresets(targetType, engineKey);
    if (!presets.length) return;

    const props = this._getTargetProperties();
    let added = 0;
    for (const preset of presets) {
      // Skip properties that already exist
      if (props[preset.name] !== undefined) continue;
      props[preset.name] = { type: preset.type, value: preset.default, description: preset.description };
      added++;
    }

    if (added === 0) return;
    this._setTargetProperties(props);
    this.state.set('dirty', true);
    this._renderProperties(props);
  }

  _createPropertyRow(name, type, value, description) {
    const row = el('div', { className: 'prop-row' });

    const tooltip = description || name;
    const nameEl = el('span', { className: 'prop-name', title: tooltip }, name);

    let inputEl;
    switch (type) {
      case 'bool':
      case 'boolean':
        inputEl = el('input', { type: 'checkbox', className: 'prop-input' });
        inputEl.checked = !!value;
        inputEl.addEventListener('change', () => this._updateProperty(name, 'bool', inputEl.checked));
        break;
      case 'int':
      case 'float':
      case 'number':
        inputEl = el('input', { type: 'number', className: 'prop-input', value: String(value || 0), step: type === 'int' ? '1' : '0.1' });
        inputEl.addEventListener('change', () => this._updateProperty(name, type, parseFloat(inputEl.value)));
        break;
      case 'color':
        inputEl = el('input', { type: 'color', className: 'prop-input', value: value || '#000000' });
        inputEl.addEventListener('change', () => this._updateProperty(name, 'color', inputEl.value));
        break;
      default: // string
        inputEl = el('input', { type: 'text', className: 'prop-input', value: String(value || '') });
        inputEl.addEventListener('change', () => this._updateProperty(name, 'string', inputEl.value));
    }

    const deleteBtn = el('button', { className: 'prop-delete', onClick: () => this._deleteProperty(name) }, '\u00D7');

    row.append(nameEl, inputEl, deleteBtn);
    return row;
  }

  _updateProperty(name, type, value) {
    if (!this._target) return;
    const props = this._getTargetProperties();
    // Preserve description if it exists
    const existing = props[name];
    const description = existing?.description || this._lookupDescription(name);
    props[name] = { type, value, ...(description ? { description } : {}) };
    this._setTargetProperties(props);
    this.state.set('dirty', true);
  }

  _deleteProperty(name) {
    if (!this._target) return;
    const props = this._getTargetProperties();
    delete props[name];
    this._setTargetProperties(props);
    this.state.set('dirty', true);
    this._renderProperties(props);
  }

  /** Show a searchable dropdown of known property names instead of a blank prompt. */
  _addPropertyWithSuggestions() {
    if (!this._target) return;

    const existingProps = this._getTargetProperties();
    const suggestions = this._getSuggestedProperties(existingProps);

    // If no suggestions, fall back to simple prompt
    if (!suggestions.length) {
      this._addPropertyManual();
      return;
    }

    // Build a modal picker
    const overlay = el('div', { className: 'prop-picker-overlay' });
    const dialog = el('div', { className: 'prop-picker-dialog' });

    const title = el('div', { className: 'prop-picker-title' }, 'Add Property');
    dialog.appendChild(title);

    // Search input
    const searchInput = el('input', {
      type: 'text',
      className: 'prop-picker-search',
      placeholder: 'Search or type custom name...',
    });
    dialog.appendChild(searchInput);

    // Suggestion list
    const listContainer = el('div', { className: 'prop-picker-list' });

    const renderList = (filter) => {
      listContainer.innerHTML = '';
      const term = (filter || '').toLowerCase();
      const filtered = term
        ? suggestions.filter(s => s.name.toLowerCase().includes(term) || s.description.toLowerCase().includes(term))
        : suggestions;

      for (const s of filtered) {
        const item = el('div', {
          className: 'prop-picker-item',
          title: s.description,
          onClick: () => {
            this._addPresetProperty(s);
            overlay.remove();
          },
        });
        item.appendChild(el('span', { className: 'prop-picker-item-name' }, s.name));
        item.appendChild(el('span', { className: 'prop-picker-item-type' }, s.type));
        if (s.description) {
          item.appendChild(el('div', { className: 'prop-picker-item-desc' }, s.description));
        }
        listContainer.appendChild(item);
      }

      // Always show "Custom property" option at the bottom
      if (term) {
        const customItem = el('div', {
          className: 'prop-picker-item prop-picker-custom',
          onClick: () => {
            overlay.remove();
            this._addPropertyManualWithName(term);
          },
        });
        customItem.appendChild(el('span', { className: 'prop-picker-item-name' }, `"${term}" (custom)`));
        customItem.appendChild(el('span', { className: 'prop-picker-item-type' }, 'string'));
        listContainer.appendChild(customItem);
      }
    };

    renderList('');

    searchInput.addEventListener('input', () => renderList(searchInput.value));
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') overlay.remove();
      if (e.key === 'Enter') {
        const val = searchInput.value.trim();
        if (!val) return;
        // Check if it matches a suggestion exactly
        const match = suggestions.find(s => s.name.toLowerCase() === val.toLowerCase());
        if (match) {
          this._addPresetProperty(match);
        } else {
          this._addPropertyManualWithName(val);
        }
        overlay.remove();
      }
    });

    dialog.appendChild(listContainer);

    // Cancel button
    const cancelBtn = el('button', {
      className: 'btn btn-sm',
      onClick: () => overlay.remove(),
    }, 'Cancel');
    dialog.appendChild(cancelBtn);

    overlay.appendChild(dialog);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
    document.body.appendChild(overlay);

    // Focus search input
    requestAnimationFrame(() => searchInput.focus());
  }

  /** Get suggested properties for the current target, excluding already-set ones. */
  _getSuggestedProperties(existingProps) {
    if (!this._target) return [];
    const targetType = this._target.type;
    const targetEngine = this.state.get('targetEngine');
    const resolvedKey = resolvePresetKey(targetEngine);

    // Collect from resolved engine + generic, deduplicating
    const seen = new Set(Object.keys(existingProps));
    const results = [];

    const addPresets = (engineKey) => {
      const presets = getPresets(targetType, engineKey);
      for (const p of presets) {
        if (seen.has(p.name)) continue;
        seen.add(p.name);
        results.push(p);
      }
    };

    // Engine-specific first
    if (resolvedKey && resolvedKey !== 'generic') {
      addPresets(resolvedKey);
    }
    // Then generic
    addPresets('generic');

    return results;
  }

  /** Look up a description for a property name from all presets. */
  _lookupDescription(name) {
    if (!this._target) return '';
    const targetType = this._target.type;
    const group = PROPERTY_PRESETS[targetType];
    if (!group) return '';
    for (const presets of Object.values(group)) {
      const match = presets.find(p => p.name === name);
      if (match) return match.description;
    }
    return '';
  }

  /** Add a property from a preset definition. */
  _addPresetProperty(preset) {
    const props = this._getTargetProperties();
    if (props[preset.name] !== undefined) return;
    props[preset.name] = { type: preset.type, value: preset.default, description: preset.description };
    this._setTargetProperties(props);
    this.state.set('dirty', true);
    this._renderProperties(props);
  }

  /** Fallback: add property with a manual name prompt. */
  _addPropertyManual() {
    const name = prompt('Property name:');
    if (!name) return;
    this._addPropertyManualWithName(name);
  }

  /** Add a custom-named property with type prompt. */
  _addPropertyManualWithName(name) {
    const type = prompt('Type (string, int, float, bool, color):', 'string') || 'string';
    const props = this._getTargetProperties();
    props[name] = { type, value: type === 'bool' ? false : type === 'int' || type === 'float' ? 0 : '' };
    this._setTargetProperties(props);
    this.state.set('dirty', true);
    this._renderProperties(props);
  }

  _getTargetProperties() {
    if (!this._target) return {};
    if (this._target.type === 'map') return this.state.get('properties') || {};
    return this._target.ref.properties || {};
  }

  _setTargetProperties(props) {
    if (!this._target) return;
    if (this._target.type === 'map') {
      this.state.set('properties', props);
    } else {
      this._target.ref.properties = props;
      this.state._notify('layers', this.state.get('layers'), this.state.get('layers'));
    }
  }
}
